import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { Settings, ShieldCheck } from 'lucide-react';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import StudySectionIcon from '../StudySectionIcon';
import { secureAuthService } from '../../../services/secureAuthService';
import {
  EVENTS_MAX_LIMIT,
  ensureConversations,
  getConversationKeys,
  getMyKeys,
  getUnread,
  listConversations,
  listMessages,
  listSecurityEvents,
  markRead,
  resetConversationEncryption,
  sendMessage,
  subscribeToStudyStream,
  uploadGrants,
} from '../../../services/chat/chatService';
import type {
  ChatMessage,
  Conversation,
  KeyGrantUpload,
  MemberKeyInfo,
  MyKeyGrant,
  SecurityEvent,
  UserKeys,
} from '../../../services/chat/chatService';
import {
  decryptMessage,
  encryptMessage,
  formatFingerprint,
  generateConversationKey,
  keyFingerprint,
  unwrapConversationKey,
} from '../../../services/chat/chatCrypto';
import { getSessionKeys, holdSessionKeys, lockKeys } from '../../../services/chat/chatKeys';
import type { SessionKeys } from '../../../services/chat/chatKeys';
import { PASSKEY_UNSUPPORTED_MESSAGE } from '../../../services/chat/chatPasskeys';
import {
  bootstrapGrantees,
  buildGrantsForMember,
  grantErrorCode,
  holderForEpoch,
  membersAwaitingApproval,
  membersForAutomaticGrant,
  systemLineText,
  versionsToGrant,
} from '../../../services/chat/chatGrants';
import { startIdleLock } from '../../../services/chat/chatIdleLock';
import {
  deleteTrustedRecord,
  reunlockFromDeviceKey,
  silentUnlock,
} from '../../../services/chat/chatTrustedBrowser';
import { KeySetupDialog } from './KeySetupDialog';
import { UnlockGate } from './UnlockGate';
import { ConversationList } from './ConversationList';
import type { ChatConversationSummary } from './ConversationList';
import { ChatThread } from './ChatThread';
import type { ChatThreadMessage, ChatThreadNotice } from './ChatThread';
import { EncryptionResetDialog } from './EncryptionResetDialog';
import { isReauthRequired, SettingsGateCancelled, useSettingsGate } from './useSettingsGate';
import type { EncryptionResetPhase } from './EncryptionResetDialog';
import { KeyChangeApprovalDialog } from './KeyChangeApprovalDialog';
import { EncryptionInfoDialog } from './EncryptionInfoDialog';
import { ChatKeySettings } from './ChatKeySettings';

// Presentational pieces moved to ConversationList.tsx / ChatThread.tsx; re-exported
// so existing imports from './ChatTab' keep working.
export type { ChatConversationSummary } from './ConversationList';
export type { ChatThreadMessage } from './ChatThread';

/** Exact copy shown for a conversation whose participant runs an app version without chat. */
export const PARTICIPANT_APP_OUTDATED_MESSAGE =
  'Participant needs to update their app in order to have the chat feature.';

/** Waiting for any key-holding client's automatic grant (exact copy). */
export const ACCESS_PENDING_MESSAGE = 'Access pending';

/** The researcher's own key is `keyChangePending` in this conversation (exact copy). */
export const SELF_PENDING_BANNER =
  'A colleague must confirm your encryption key before this conversation can be read.';

/** Shown to the study owner under the self-pending banner. */
export const OWNER_SELF_PENDING_LINE =
  'History returns if another researcher joins this study and confirms your key.';

/** Shared missing-grant placeholder (the copy the mobile clients ship). */
export const UNDECRYPTABLE_MESSAGE_PLACEHOLDER = "This message can't be displayed on this device.";

/** Fixed copy for ciphertext from an epoch retired by an owner emergency reset. */
export const ENCRYPTION_RESET_MESSAGE_PLACEHOLDER = 'Message unavailable after encryption reset.';

export const KEY_VERSION_CONFLICT_MESSAGE =
  'This conversation changed before the reset. Close this dialog, reload Chat, and try again.';

/** The reset landed, but this browser could not fetch and unwrap its own grant for the new epoch. */
export const RESET_UNLOCK_FAILED_MESSAGE =
  'Encryption was reset, but this browser could not load the new key yet. Retry to continue.';

export const IDLE_LOCKED_MESSAGE = 'Chat locked after 15 minutes of inactivity. Click or press a key to continue.';

const PAGE_SIZE = 50;
const REPLACED_NOTICE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

type GateState = 'idle' | 'checking' | 'setup' | 'locked' | 'ready' | 'error';

interface ConvKeyState {
  status: 'loading' | 'ready' | 'pending' | 'self-pending' | 'error';
  error?: string;
}

/** A decrypted (or placeholder) message, or a system line, as the thread renders it. */
interface UiMessage {
  id: string;
  senderId: string;
  senderRole: 'researcher' | 'participant';
  text: string;
  createdAt: string;
  keyVersion: number;
  undecryptable?: boolean;
  failed?: boolean;
  system?: boolean;
}

interface ApprovalRequest {
  conversationId: string;
  uid: string;
  publicKey: string;
  displayName: string;
  fingerprint: string;
}

function newMessageId(): string {
  const cryptoApi: any = (globalThis as any).crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  // Fallback for environments without WebCrypto randomUUID (older jsdom) — v4 shape.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const parseTime = (iso: string | null | undefined): number => (iso ? Date.parse(iso) : 0);

/** Most recently active first (server order also does this; re-applied after stream updates). */
function sortConversations(conversations: Conversation[]): Conversation[] {
  return [...conversations].sort(
    (a, b) => parseTime(b.lastMessageAt ?? b.createdAt) - parseTime(a.lastMessageAt ?? a.createdAt),
  );
}

/** Messages and system lines merged by createdAt (stable for equal timestamps). */
function sortThread(items: UiMessage[]): UiMessage[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => parseTime(a.item.createdAt) - parseTime(b.item.createdAt) || a.index - b.index)
    .map(({ item }) => item);
}

/** Unread = someone else sent the last message and my read marker hasn't passed it. */
function isConversationUnread(conversation: Conversation, uid: string): boolean {
  if (!conversation.lastMessageAt || conversation.lastMessageSenderId === uid) return false;
  return parseTime(conversation.readState?.[uid]) < parseTime(conversation.lastMessageAt);
}

function eventToUi(event: SecurityEvent): UiMessage | null {
  const text = systemLineText(event);
  if (!text) return null;
  return {
    id: `event:${event.id}`,
    senderId: event.actorUid,
    senderRole: 'researcher',
    text,
    createdAt: event.createdAt,
    keyVersion: 0,
    system: true,
  };
}

/**
 * "Your chat key was replaced on <date>…" for the unlock gate: the entry whose
 * `publicKey` equals the invalidated record's key, else any entry from the
 * last 30 days (the latest one).
 */
export function replacedKeyNotice(
  keys: UserKeys,
  recordPublicKey: string | null,
  now: number = Date.now(),
): string | null {
  const byRecord = recordPublicKey ? keys.keyHistory.find((entry) => entry.publicKey === recordPublicKey) : undefined;
  const recent = [...keys.keyHistory]
    .filter((entry) => now - parseTime(entry.replacedAt) <= REPLACED_NOTICE_WINDOW_MS)
    .sort((a, b) => parseTime(b.replacedAt) - parseTime(a.replacedAt))[0];
  const entry = byRecord ?? recent;
  if (!entry) return null;
  const date = new Date(entry.replacedAt).toLocaleDateString();
  return `Your chat key was replaced on ${date}. If this was not you, contact the study owner before continuing.`;
}

export interface ChatTabProps {
  studyId?: string;
  /** UI visibility only; the backend independently enforces study ownership. */
  isOwner?: boolean;
  /** Participant display names by participantId; a conversation shows "Name (ID)" when present. */
  participantNames?: Record<string, string>;
  /**
   * Whether the Chat tab is the visible dashboard tab. The tab stays mounted
   * inside a KeepMountedTabPanel, so without this flag the key-setup dialog (a
   * portal) would appear over the dashboard before the user ever opens Chat.
   * The gate flow starts on first activation; undefined means "always active"
   * (standalone/test usage).
   */
  active?: boolean;
  /** Reports the number of unread conversations (for the dashboard tab badge). */
  onUnreadCountChange?: (count: number) => void;
}

/**
 * Researcher ↔ participant E2E-encrypted chat container.
 *
 * Key gate on first activation (`GET /keys/me` → setup dialog, trusted-browser
 * silent unlock, or the passkey/recovery-key unlock gate), then conversations
 * via ensureConversations/listConversations with the study SSE stream for live
 * updates. Per selected conversation it applies the own-entry precedence
 * (self-pending banner → bootstrap → access pending → unwrap), grants
 * non-pending members automatically, queues one approval dialog per pending
 * (member, key), merges security events into the thread, and drives read
 * receipts/unread badges from read-state metadata. Conversation keys live only
 * in a ref (memory) and are cleared by the 15-minute idle lock.
 */
export function ChatTab({ studyId = '', isOwner = false, participantNames, active, onUnreadCountChange }: ChatTabProps) {
  const currentUser = useMemo(() => secureAuthService.getCurrentUser(), []);
  const uid = currentUser?.uid ?? '';
  const email = currentUser?.email || uid;

  const participantLabel = (participantId: string) => {
    const name = participantNames?.[participantId]?.trim();
    return name ? `${name} (${participantId})` : `Participant ${participantId}`;
  };
  const [gate, setGate] = useState<GateState>('idle');
  const [armed, setArmed] = useState(active !== false);
  const [gateAttempt, setGateAttempt] = useState(0);
  const [gateError, setGateError] = useState<string | null>(null);
  const [myKeys, setMyKeys] = useState<UserKeys | null>(null);
  // Settings gate for the owner reset: recent assertion → passkey prompt → fresh sign-in.
  const resetGate = useSettingsGate({ uid, email, passkeyCount: myKeys?.passkeys.length ?? 0 });
  const [replacedNotice, setReplacedNotice] = useState<string | null>(null);
  const [idleLocked, setIdleLocked] = useState(false);
  const [sessionEpoch, setSessionEpoch] = useState(0);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [resolveNonce, setResolveNonce] = useState(0);

  const [keyStates, setKeyStates] = useState<Record<string, ConvKeyState>>({});
  const [membersInfo, setMembersInfo] = useState<Record<string, MemberKeyInfo[]>>({});
  const [threads, setThreads] = useState<Record<string, UiMessage[]>>({});
  const [hasMore, setHasMore] = useState<Record<string, boolean>>({});
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [unreadIds, setUnreadIds] = useState<ReadonlySet<string>>(new Set());

  const [resetOpen, setResetOpen] = useState(false);
  const [resetPhase, setResetPhase] = useState<EncryptionResetPhase>('confirm');
  // Key version the owner saw when the reset dialog opened; null while the dialog is closed. It is
  // sent as expectedKeyVersion so a reset made elsewhere in the meantime answers 409 instead of
  // resetting the conversation a second time.
  const [resetExpectedVersion, setResetExpectedVersion] = useState<number | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [approvalQueue, setApprovalQueue] = useState<ApprovalRequest[]>([]);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  // Unwrapped conversation keys, keyed conversationId -> keyVersion -> CK.
  // A ref, never state/storage: memory only, and updates must not re-render.
  const ckCacheRef = useRef(new Map<string, Map<number, Uint8Array>>());
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const gateRef = useRef(gate);
  gateRef.current = gate;
  const activeRef = useRef(active !== false);
  activeRef.current = active !== false;
  const idleLockedRef = useRef(false);
  const myKeysRef = useRef(myKeys);
  myKeysRef.current = myKeys;
  const keyStatesRef = useRef(keyStates);
  keyStatesRef.current = keyStates;
  const membersRef = useRef(membersInfo);
  membersRef.current = membersInfo;
  // Approval bookkeeping per conversation: keys shown since the last open, keys
  // suppressed by "Not now" until the next open, and the one-time listing refetch
  // after grant_invalid / grantee_pending.
  const shownApprovalsRef = useRef(new Map<string, Set<string>>());
  const suppressedApprovalsRef = useRef(new Map<string, Set<string>>());
  const refetchedOnceRef = useRef(new Set<string>());
  /** Conversations whose SSE frame landed while their keys were still resolving; applied when that resolve completes. */
  const pendingRefreshRef = useRef(new Set<string>());

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  const decryptToUi = useCallback(
    async (conversationId: string, message: ChatMessage): Promise<UiMessage> => {
      const base = {
        id: message.id,
        senderId: message.senderId,
        senderRole: message.senderRole,
        createdAt: message.createdAt,
        keyVersion: message.keyVersion,
      };
      const readableFloor = conversationsRef.current.find((c) => c.id === conversationId)
        ?.minimumReadableKeyVersion ?? 1;
      if (message.keyVersion < readableFloor) {
        return { ...base, text: ENCRYPTION_RESET_MESSAGE_PLACEHOLDER, undecryptable: true };
      }
      const ck = ckCacheRef.current.get(conversationId)?.get(message.keyVersion);
      if (!ck) return { ...base, text: UNDECRYPTABLE_MESSAGE_PLACEHOLDER, undecryptable: true };
      try {
        const text = await decryptMessage(ck, message, {
          conversationId,
          senderId: message.senderId,
          messageId: message.id,
          keyVersion: message.keyVersion,
        });
        return { ...base, text };
      } catch {
        // Tampered/foreign ciphertext must never crash the thread — placeholder row instead.
        return { ...base, text: UNDECRYPTABLE_MESSAGE_PLACEHOLDER, undecryptable: true };
      }
    },
    [],
  );

  /**
   * Evict retired CKs and replace any already-rendered retired ciphertext with fixed safe copy.
   * A floor this client already applied is a no-op: the stream event and the reset response both
   * announce the same reset, and the second one must not send a finished key load back to loading.
   */
  const applyReadableFloor = useCallback((
    conversationId: string,
    minimumReadableKeyVersion: number,
  ) => {
    const known = conversationsRef.current.find((c) => c.id === conversationId);
    if (!known || (known.minimumReadableKeyVersion ?? 1) >= minimumReadableKeyVersion) return;
    const versions = ckCacheRef.current.get(conversationId);
    if (versions) {
      for (const version of Array.from(versions.keys())) {
        if (version < minimumReadableKeyVersion) versions.delete(version);
      }
      if (versions.size === 0) ckCacheRef.current.delete(conversationId);
    }
    setThreads((prev) => {
      const thread = prev[conversationId];
      if (!thread) return prev;
      let changed = false;
      const nextThread = thread.map((message) => {
        if (message.system || message.keyVersion >= minimumReadableKeyVersion
          || message.text === ENCRYPTION_RESET_MESSAGE_PLACEHOLDER) return message;
        changed = true;
        return {
          ...message,
          text: ENCRYPTION_RESET_MESSAGE_PLACEHOLDER,
          undecryptable: true,
        };
      });
      return changed ? { ...prev, [conversationId]: nextThread } : prev;
    });
    setKeyStates((prev) => ({ ...prev, [conversationId]: { status: 'loading' } }));
  }, []);

  const markConversationRead = useCallback(
    (conversationId: string) => {
      setUnreadIds((prev) => {
        if (!prev.has(conversationId)) return prev;
        const next = new Set(prev);
        next.delete(conversationId);
        return next;
      });
      markRead(studyId, conversationId)
        .then((entry) => {
          setConversations((prev) =>
            prev.map((c) =>
              c.id === conversationId
                ? { ...c, readState: { ...(c.readState ?? {}), [entry.uid]: entry.lastReadAt } }
                : c,
            ),
          );
        })
        .catch(() => {
          /* best-effort; the badge self-corrects from the stream / next load */
        });
    },
    [studyId],
  );

  /** Append a live stream message to a loaded thread, or to the open thread while its first page loads. */
  const appendStreamMessage = useCallback(
    (conversationId: string, message: ChatMessage) => {
      if (idleLockedRef.current) return; // locked: unread counts only, nothing decrypted or rendered
      // Not loaded and not readable in the open thread — the first page picks it up. (With the CK
      // cached the open thread's page is in flight: keep the row and let the page merge around it.)
      const loaded = Boolean(threadsRef.current[conversationId]);
      const openAndReadable =
        selectedIdRef.current === conversationId &&
        ckCacheRef.current.get(conversationId)?.has(message.keyVersion) === true;
      if (!loaded && !openAndReadable) return;
      void decryptToUi(conversationId, message).then((ui) => {
        if (idleLockedRef.current) return;
        setThreads((prev) => {
          const thread = prev[conversationId] ?? [];
          if (thread.some((m) => m.id === ui.id)) return prev;
          return { ...prev, [conversationId]: [...thread, ui] };
        });
      });
    },
    [decryptToUi],
  );

  const appendSystemLine = useCallback((conversationId: string, event: SecurityEvent) => {
    if (idleLockedRef.current) return;
    const ui = eventToUi(event);
    if (!ui) return;
    setThreads((prev) => {
      const thread = prev[conversationId];
      if (!thread || thread.some((m) => m.id === ui.id)) return prev;
      return { ...prev, [conversationId]: [...thread, ui] };
    });
  }, []);

  const setMembers = useCallback((conversationId: string, members: MemberKeyInfo[]) => {
    setMembersInfo((prev) => ({ ...prev, [conversationId]: members }));
  }, []);

  const setKeyState = useCallback((conversationId: string, state: ConvKeyState) => {
    setKeyStates((prev) => ({ ...prev, [conversationId]: state }));
  }, []);

  /** Queue an approval dialog for every pending key not shown or suppressed since the conversation was last opened. */
  const queueApprovals = useCallback(
    (conversationId: string, members: MemberKeyInfo[]) => {
      const shown = shownApprovalsRef.current.get(conversationId) ?? new Set<string>();
      const suppressed = suppressedApprovalsRef.current.get(conversationId) ?? new Set<string>();
      const fresh = membersAwaitingApproval(members, uid).filter((member) => {
        const key = `${member.uid}:${member.publicKey}`;
        return !shown.has(key) && !suppressed.has(key);
      });
      if (fresh.length === 0) return;
      fresh.forEach((member) => shown.add(`${member.uid}:${member.publicKey}`));
      shownApprovalsRef.current.set(conversationId, shown);
      void Promise.all(
        fresh.map(async (member) => ({
          conversationId,
          uid: member.uid,
          publicKey: member.publicKey as string,
          displayName: member.displayName || member.uid,
          fingerprint: formatFingerprint(await keyFingerprint(member.publicKey as string)),
        })),
      ).then((requests) => {
        if (selectedIdRef.current !== conversationId) return;
        setApprovalQueue((prev) => [
          ...prev,
          ...requests.filter((r) => !prev.some((p) => p.uid === r.uid && p.publicKey === r.publicKey)),
        ]);
      });
    },
    [uid],
  );

  /** Unwrap every own grant not yet cached (old readable versions keep rotated history readable). */
  const unwrapOwnGrants = useCallback(
    async (
      conversationId: string,
      grants: MyKeyGrant[],
      versions: Map<number, Uint8Array>,
      session: SessionKeys,
      floor: number,
    ) => {
      for (const grant of grants) {
        if (grant.keyVersion < floor || versions.has(grant.keyVersion)) continue;
        try {
          versions.set(
            grant.keyVersion,
            await unwrapConversationKey(grant.wrappedCk, session.privateKey, {
              conversationId,
              granteeUid: uid,
              keyVersion: grant.keyVersion,
            }),
          );
        } catch {
          // An own grant that fails to unwrap is treated as missing for display.
        }
      }
    },
    [uid],
  );

  /** Automatic grants: every non-pending member lacking a readable version this tab holds. */
  const automaticGrantScan = useCallback(
    async (conversationId: string, members: MemberKeyInfo[], versions: Map<number, Uint8Array>, floor: number) => {
      const held = Array.from(versions.keys());
      const targets = membersForAutomaticGrant(members, held, floor, uid);
      if (targets.length === 0) return;
      const grantSets = await Promise.all(
        targets.map((member) =>
          buildGrantsForMember(versions, member, conversationId, versionsToGrant(held, member, floor)),
        ),
      );
      const grants = ([] as KeyGrantUpload[]).concat(...grantSets);
      try {
        await uploadGrants(studyId, conversationId, grants);
      } catch (err) {
        const code = grantErrorCode(err);
        if (code === 'caller_pending') {
          setKeyState(conversationId, { status: 'self-pending' });
        } else if (
          (code === 'grant_invalid' || code === 'grantee_pending' || code === 'grant_stale') &&
          !refetchedOnceRef.current.has(conversationId)
        ) {
          refetchedOnceRef.current.add(conversationId);
          try {
            setMembers(conversationId, (await getConversationKeys(studyId, conversationId)).members);
          } catch {
            /* wait for the next open or poll */
          }
        }
        // Anything else (a concurrent colleague raced us, transport) is left for the next open.
      }
    },
    [studyId, uid, setKeyState, setMembers],
  );

  /**
   * The current CK is held (a security_event for the open conversation, or a
   * re-open with the CK still cached): refetch the listing, rescan, queue approvals.
   */
  const rescanConversation = useCallback(
    async (conversationId: string) => {
      const conversation = conversationsRef.current.find((c) => c.id === conversationId);
      const versions = ckCacheRef.current.get(conversationId);
      if (!conversation || !versions?.get(conversation.keyVersion)) return;
      try {
        const keys = await getConversationKeys(studyId, conversationId);
        setMembers(conversationId, keys.members);
        if (keys.members.find((m) => m.uid === uid)?.keyChangePending) {
          setKeyState(conversationId, { status: 'self-pending' });
          return;
        }
        await automaticGrantScan(conversationId, keys.members, versions, conversation.minimumReadableKeyVersion ?? 1);
        queueApprovals(conversationId, keys.members);
      } catch {
        /* the next open rescans */
      }
    },
    [studyId, uid, setMembers, setKeyState, automaticGrantScan, queueApprovals],
  );

  /**
   * Own-entry precedence on conversation open (unlocked tab): self-pending
   * banner → bootstrap → access pending → unwrap and grant for others.
   * Resolves true when this tab holds the conversation's current CK.
   */
  const resolveConversationKeys = useCallback(
    async (conversationId: string, isCancelled: () => boolean): Promise<boolean> => {
      const conversation = conversationsRef.current.find((c) => c.id === conversationId);
      const session = getSessionKeys();
      if (!conversation || !session) return false;
      const version = conversation.keyVersion;
      const floor = conversation.minimumReadableKeyVersion ?? 1;

      const cached = ckCacheRef.current.get(conversationId);
      if (cached?.get(version)) {
        setKeyState(conversationId, { status: 'ready' });
        // Re-opened with the CK still cached: the listing is refetched on every open (grant scan, approvals).
        await rescanConversation(conversationId);
        return true;
      }
      setKeyState(conversationId, { status: 'loading' });
      try {
        let keys = await getConversationKeys(studyId, conversationId);
        if (isCancelled()) return false;
        setMembers(conversationId, keys.members);
        if (keys.members.find((m) => m.uid === uid)?.keyChangePending) {
          setKeyState(conversationId, { status: 'self-pending' });
          return false;
        }

        const versions = cached ?? new Map<number, Uint8Array>();
        await unwrapOwnGrants(conversationId, keys.myKeys, versions, session, floor);
        if (isCancelled()) return false;

        if (!versions.get(version) && !holderForEpoch(keys.members, version)) {
          // Fresh-epoch bootstrap: mint the CK and wrap it for every published, non-pending
          // member (self included). A lost race falls through to the next rule.
          const ck = generateConversationKey();
          const grants = ([] as KeyGrantUpload[]).concat(
            ...(await Promise.all(
              bootstrapGrantees(keys.members).map((member) =>
                buildGrantsForMember(new Map([[version, ck]]), member, conversationId, [version]),
              ),
            )),
          );
          try {
            await uploadGrants(studyId, conversationId, grants, { bootstrap: true });
          } catch (err) {
            if (grantErrorCode(err) === 'caller_pending') {
              setKeyState(conversationId, { status: 'self-pending' });
              return false;
            }
            // Lost (or rejected) bootstrap: the authoritative re-fetch below decides.
          }
          if (isCancelled()) return false;
          // Never trust the locally generated candidate: fetch the own grant back.
          keys = await getConversationKeys(studyId, conversationId);
          if (isCancelled()) return false;
          setMembers(conversationId, keys.members);
          if (keys.members.find((m) => m.uid === uid)?.keyChangePending) {
            setKeyState(conversationId, { status: 'self-pending' });
            return false;
          }
          await unwrapOwnGrants(conversationId, keys.myKeys, versions, session, floor);
          if (isCancelled()) return false;
        }

        ckCacheRef.current.set(conversationId, versions);
        if (!versions.get(version)) {
          setKeyState(conversationId, { status: 'pending' });
          return false;
        }

        setKeyState(conversationId, { status: 'ready' });
        await automaticGrantScan(conversationId, keys.members, versions, floor);
        if (isCancelled()) return true;
        queueApprovals(conversationId, keys.members);
        return true;
      } catch (err: any) {
        if (!isCancelled()) {
          setKeyState(conversationId, {
            status: 'error',
            error: err?.message || 'Could not load the conversation keys.',
          });
        }
        return false;
      }
    },
    [studyId, uid, setKeyState, setMembers, unwrapOwnGrants, automaticGrantScan, queueApprovals, rescanConversation],
  );

  /** First page of history plus every security event, merged by createdAt. */
  const loadFirstPage = useCallback(
    async (conversationId: string, isCancelled: () => boolean) => {
      try {
        const [page, events] = await Promise.all([
          listMessages(studyId, conversationId, { limit: PAGE_SIZE }),
          listSecurityEvents(studyId, conversationId, { limit: EVENTS_MAX_LIMIT }).catch(() => [] as SecurityEvent[]),
        ]);
        if (isCancelled()) return;
        const decrypted = await Promise.all(page.map((m) => decryptToUi(conversationId, m)));
        if (isCancelled() || idleLockedRef.current) return;
        const lines = events.map(eventToUi).filter((line): line is UiMessage => line != null);
        setThreads((prev) => {
          // Rows that landed during the fetch (a live append or an own send) stay; the page merges around them.
          const current = prev[conversationId] ?? [];
          const known = new Set(current.map((m) => m.id));
          const fresh = [...decrypted, ...lines].filter((m) => !known.has(m.id));
          return { ...prev, [conversationId]: sortThread([...fresh, ...current]) };
        });
        setHasMore((prev) => ({ ...prev, [conversationId]: page.length === PAGE_SIZE }));
      } catch {
        if (!isCancelled()) setThreads((prev) => ({ ...prev, [conversationId]: prev[conversationId] ?? [] }));
      }
    },
    [studyId, decryptToUi],
  );

  // ------------------------------------------------------------------
  // Mount: unread summary + study stream (badge stays live even pre-gate)
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!studyId) return undefined;
    let cancelled = false;

    getUnread(studyId)
      .then((summary) => {
        if (cancelled) return;
        setUnreadIds(new Set(summary.conversations.filter((c) => c.unread).map((c) => c.conversationId)));
      })
      .catch(() => {
        /* badge starts at 0; the stream init will correct it */
      });

    const subscription = subscribeToStudyStream(studyId, {
      onInit: (initConversations) => {
        setConversations(sortConversations(initConversations));
        setUnreadIds(new Set(initConversations.filter((c) => isConversationUnread(c, uid)).map((c) => c.id)));
      },
      onMessage: ({ conversationId, message }) => {
        setConversations((prev) =>
          sortConversations(
            prev.map((c) =>
              c.id === conversationId
                ? { ...c, lastMessageAt: message.createdAt, lastMessageSenderId: message.senderId }
                : c,
            ),
          ),
        );
        appendStreamMessage(conversationId, message);
        if (message.senderId !== uid) {
          const threadFocused =
            selectedIdRef.current === conversationId &&
            activeRef.current &&
            gateRef.current === 'ready' &&
            !idleLockedRef.current;
          if (threadFocused) markConversationRead(conversationId);
          else setUnreadIds((prev) => (prev.has(conversationId) ? prev : new Set(prev).add(conversationId)));
        }
      },
      onConversation: (event) => {
        const current = conversationsRef.current.find((c) => c.id === event.conversationId);
        const currentFloor = current?.minimumReadableKeyVersion ?? 1;
        const readableFloor = event.minimumReadableKeyVersion ?? currentFloor;
        applyReadableFloor(event.conversationId, readableFloor);
        setConversations((prev) =>
          sortConversations(
            prev.map((c) =>
              c.id === event.conversationId
                ? {
                    ...c,
                    keyVersion: event.keyVersion,
                    minimumReadableKeyVersion: readableFloor,
                    lastMessageAt: event.lastMessageAt,
                    lastMessageSenderId: event.lastMessageSenderId,
                    needsRotation: event.needsRotation,
                  }
                : c,
            ),
          ),
        );
      },
      onReadState: (event) => {
        setConversations((prev) =>
          prev.map((c) =>
            c.id === event.conversationId
              ? { ...c, readState: { ...(c.readState ?? {}), [event.uid]: event.lastReadAt } }
              : c,
          ),
        );
        if (event.uid === uid) {
          setUnreadIds((prev) => {
            if (!prev.has(event.conversationId)) return prev;
            const next = new Set(prev);
            next.delete(event.conversationId);
            return next;
          });
        }
      },
      onSecurityEvent: ({ conversationId, event }) => {
        appendSystemLine(conversationId, event);
        if (selectedIdRef.current !== conversationId || gateRef.current !== 'ready' || idleLockedRef.current) return;
        const status = keyStatesRef.current[conversationId]?.status;
        if (status === 'loading') pendingRefreshRef.current.add(conversationId);
        else if (status === 'pending' || status === 'self-pending') setResolveNonce((n) => n + 1);
        else void rescanConversation(conversationId);
      },
      onMemberKey: ({ conversationId, granteeUid }) => {
        if (granteeUid !== uid) return;
        if (selectedIdRef.current !== conversationId || gateRef.current !== 'ready' || idleLockedRef.current) return;
        const status = keyStatesRef.current[conversationId]?.status;
        if (status === 'loading') pendingRefreshRef.current.add(conversationId);
        else if (status === 'pending' || status === 'self-pending') setResolveNonce((n) => n + 1);
      },
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [studyId, uid, appendStreamMessage, appendSystemLine, markConversationRead, applyReadableFloor, rescanConversation]);

  useEffect(() => {
    onUnreadCountChange?.(unreadIds.size);
  }, [unreadIds, onUnreadCountChange]);

  // ------------------------------------------------------------------
  // Key gate: starts on first activation (KeepMountedTabPanel mounts us early)
  // ------------------------------------------------------------------

  useEffect(() => {
    if (active !== false) setArmed(true);
  }, [active]);

  useEffect(() => {
    if (!armed) return undefined;
    let cancelled = false;
    setGate('checking');
    (async () => {
      try {
        const keys = await getMyKeys();
        if (cancelled) return;
        if (!keys) {
          await deleteTrustedRecord(uid);
          if (!cancelled) setGate('setup');
          return;
        }
        setMyKeys(keys);
        if (getSessionKeys()) {
          setGate('ready');
          return;
        }
        const result = await silentUnlock(uid, keys);
        if (cancelled) return;
        if (result.status === 'unlocked') {
          holdSessionKeys(keys.publicKey, result.privateKey, 'device');
          setSessionEpoch((n) => n + 1);
          setGate('ready');
        } else {
          setReplacedNotice(replacedKeyNotice(keys, result.status === 'invalidated' ? result.recordPublicKey : null));
          setGate('locked');
        }
      } catch (err: any) {
        if (cancelled) return;
        setGateError(err?.message || 'Could not check your chat encryption keys.');
        setGate('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [armed, gateAttempt, uid]);

  // ------------------------------------------------------------------
  // Idle lock: 15 minutes without input or hidden → clear keys, CKs, plaintext;
  // on return re-unwrap from the device key immediately (no GET /keys/me)
  // ------------------------------------------------------------------

  useEffect(() => {
    if (gate !== 'ready') return undefined;
    return startIdleLock({
      onLock: () => {
        lockKeys();
        ckCacheRef.current.clear();
        idleLockedRef.current = true;
        setThreads({});
        setKeyStates({});
        setApprovalQueue([]);
        setIdleLocked(true);
      },
      onResume: () => {
        void (async () => {
          const keys = myKeysRef.current;
          const privateKey = keys ? await reunlockFromDeviceKey(uid) : null;
          idleLockedRef.current = false;
          setIdleLocked(false);
          if (keys && privateKey) {
            holdSessionKeys(keys.publicKey, privateKey, 'device');
            setSessionEpoch((n) => n + 1);
          } else {
            // The record expired or is gone: the gate still names a key replaced in the last 30 days.
            setReplacedNotice(keys ? replacedKeyNotice(keys, null) : null);
            setGate('locked');
          }
        })();
      },
    });
  }, [gate, uid]);

  // ------------------------------------------------------------------
  // Ready: provision + list conversations, auto-select the most recent
  // ------------------------------------------------------------------

  useEffect(() => {
    if (gate !== 'ready' || !studyId) return undefined;
    let cancelled = false;
    setLoadingList(true);
    setListError(null);
    (async () => {
      try {
        await ensureConversations(studyId);
        const listed = await listConversations(studyId);
        if (cancelled) return;
        const sorted = sortConversations(listed);
        setConversations(sorted);
        setSelectedId((prev) => prev ?? sorted[0]?.id ?? null);
      } catch (err: any) {
        if (!cancelled) setListError(err?.message || 'Could not load conversations.');
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gate, studyId]);

  // ------------------------------------------------------------------
  // Selected conversation: keys (precedence), grant scan, approvals,
  // first message page + events, read marker
  // ------------------------------------------------------------------

  const selectedConversation = conversations.find((c) => c.id === selectedId) ?? null;
  const selectedKeyVersion = selectedConversation?.keyVersion;

  // Opening a conversation resets the per-open approval bookkeeping.
  useEffect(() => {
    if (!selectedId) return;
    shownApprovalsRef.current.delete(selectedId);
    suppressedApprovalsRef.current.delete(selectedId);
    refetchedOnceRef.current.delete(selectedId);
    setApprovalQueue([]);
    setApprovalError(null);
  }, [selectedId]);

  useEffect(() => {
    if (gate !== 'ready' || idleLocked || !studyId || !selectedId || selectedKeyVersion == null) return undefined;
    let cancelled = false;
    const isCancelled = () => cancelled;

    (async () => {
      pendingRefreshRef.current.delete(selectedId);
      const ready = await resolveConversationKeys(selectedId, isCancelled);
      if (cancelled) return;
      if (pendingRefreshRef.current.delete(selectedId)) {
        // A frame for this conversation landed while its keys were resolving: apply it now.
        if (ready) void rescanConversation(selectedId);
        else setResolveNonce((n) => n + 1);
      }
      if (ready && !threadsRef.current[selectedId]) await loadFirstPage(selectedId, isCancelled);
      if (!cancelled && ready && activeRef.current) markConversationRead(selectedId);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    gate,
    idleLocked,
    sessionEpoch,
    resolveNonce,
    studyId,
    selectedId,
    selectedKeyVersion,
    resolveConversationKeys,
    rescanConversation,
    loadFirstPage,
    markConversationRead,
  ]);

  // Returning to the tab with an unread selected thread counts as reading it.
  useEffect(() => {
    if (active && gate === 'ready' && !idleLocked && selectedId && unreadIds.has(selectedId)) {
      markConversationRead(selectedId);
    }
  }, [active, gate, idleLocked, selectedId, unreadIds, markConversationRead]);

  const selectedMembers = selectedId ? membersInfo[selectedId] : undefined;

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------

  const handleLoadOlder = useCallback(async () => {
    const conversationId = selectedIdRef.current;
    if (!conversationId || loadingOlder) return;
    const thread = threadsRef.current[conversationId];
    const oldest = thread?.find((m) => !m.system);
    if (!oldest) return;
    setLoadingOlder(true);
    try {
      const page = await listMessages(studyId, conversationId, { limit: PAGE_SIZE, before: oldest.createdAt });
      const decrypted = await Promise.all(page.map((m) => decryptToUi(conversationId, m)));
      setThreads((prev) => {
        const current = prev[conversationId];
        if (!current) return prev;
        const known = new Set(current.map((m) => m.id));
        return {
          ...prev,
          [conversationId]: sortThread([...decrypted.filter((m) => !known.has(m.id)), ...current]),
        };
      });
      setHasMore((prev) => ({ ...prev, [conversationId]: page.length === PAGE_SIZE }));
    } catch {
      /* leave the thread as-is; the button stays for a retry */
    } finally {
      setLoadingOlder(false);
    }
  }, [studyId, loadingOlder, decryptToUi]);

  const handleSend = useCallback(
    async (text: string) => {
      const conversationId = selectedIdRef.current;
      const conversation = conversationsRef.current.find((c) => c.id === conversationId);
      if (!conversationId || !conversation) return;
      const ck = ckCacheRef.current.get(conversationId)?.get(conversation.keyVersion);
      if (!ck) return;

      const messageId = newMessageId();
      const optimistic: UiMessage = {
        id: messageId,
        senderId: uid,
        senderRole: 'researcher',
        text,
        createdAt: new Date().toISOString(),
        keyVersion: conversation.keyVersion,
      };
      setThreads((prev) => ({ ...prev, [conversationId]: [...(prev[conversationId] ?? []), optimistic] }));

      try {
        const envelope = await encryptMessage(ck, text, {
          conversationId,
          senderId: uid,
          messageId,
          keyVersion: conversation.keyVersion,
        });
        const saved = await sendMessage(studyId, conversationId, { ...envelope, messageId });
        setThreads((prev) => {
          const thread = prev[conversationId];
          if (!thread) return prev;
          return {
            ...prev,
            [conversationId]: thread.map((m) => (m.id === messageId ? { ...m, createdAt: saved.createdAt } : m)),
          };
        });
        setConversations((prev) =>
          sortConversations(
            prev.map((c) =>
              c.id === conversationId ? { ...c, lastMessageAt: saved.createdAt, lastMessageSenderId: uid } : c,
            ),
          ),
        );
      } catch {
        setThreads((prev) => {
          const thread = prev[conversationId];
          if (!thread) return prev;
          return {
            ...prev,
            [conversationId]: thread.map((m) => (m.id === messageId ? { ...m, failed: true } : m)),
          };
        });
      }
    },
    [studyId, uid],
  );

  /** The owner's bootstrap set for `expectedKeyVersion + 1`: own current key plus every published, non-pending member. */
  const buildResetGrants = useCallback(
    async (conversationId: string, members: MemberKeyInfo[], newVersion: number, ck: Uint8Array) => {
      const grantees = members.filter((m) => m.publicKey && (m.uid === uid || !m.keyChangePending));
      const sets = await Promise.all(
        grantees.map((member) => buildGrantsForMember(new Map([[newVersion, ck]]), member, conversationId, [newVersion])),
      );
      return ([] as KeyGrantUpload[]).concat(...sets);
    },
    [uid],
  );

  /** "Resetting encryption…" ends only once this tab has fetched and unwrapped its own grant for the new epoch. */
  const finishResetUnlock = useCallback(
    async (conversationId: string) => {
      if (await resolveConversationKeys(conversationId, () => false)) setResetPhase('done');
      else setResetError(RESET_UNLOCK_FAILED_MESSAGE);
    },
    [resolveConversationKeys],
  );

  const handleResetRetry = useCallback(() => {
    const conversationId = selectedIdRef.current;
    if (!conversationId || resetPhase !== 'resetting') return;
    setResetError(null);
    void finishResetUnlock(conversationId);
  }, [finishResetUnlock, resetPhase]);

  const handleEncryptionReset = useCallback(async () => {
    const conversationId = selectedIdRef.current;
    const conversation = conversationsRef.current.find((c) => c.id === conversationId);
    if (!isOwner || !conversationId || !conversation || resetPhase !== 'confirm' || resetExpectedVersion == null) return;

    setResetError(null);
    try {
      await resetGate.ensureGate();
    } catch (err: any) {
      if (!(err instanceof SettingsGateCancelled)) setResetError(err?.message || 'Could not reset this conversation. Please try again.');
      return;
    }
    setResetPhase('resetting');
    try {
      const expectedKeyVersion = resetExpectedVersion;
      const newVersion = expectedKeyVersion + 1;
      const ck = generateConversationKey();
      let members = (await getConversationKeys(studyId, conversationId)).members;
      setMembers(conversationId, members);
      let grants = await buildResetGrants(conversationId, members, newVersion, ck);
      // A 403 reauth_required re-runs the gate once (the reset did not happen), then resubmits.
      const submit = async (set: typeof grants) => {
        try {
          return await resetConversationEncryption(studyId, conversationId, expectedKeyVersion, set);
        } catch (err) {
          if (!isReauthRequired(err)) throw err;
          await resetGate.ensureGate({ force: true });
          return resetConversationEncryption(studyId, conversationId, expectedKeyVersion, set);
        }
      };
      let result;
      try {
        result = await submit(grants);
      } catch (err) {
        const code = grantErrorCode(err);
        if (code !== 'grant_stale' && code !== 'grantee_pending') throw err;
        // grant_stale: rebuild from a fresh listing; grantee_pending: the fresh listing drops
        // members that are now pending. Either way, resubmit once.
        members = (await getConversationKeys(studyId, conversationId)).members;
        setMembers(conversationId, members);
        grants = await buildResetGrants(conversationId, members, newVersion, ck);
        result = await submit(grants);
      }
      applyReadableFloor(conversationId, result.newKeyVersion);
      setConversations((prev) => prev.map((item) => (
        item.id === conversationId
          ? {
              ...item,
              keyVersion: Math.max(item.keyVersion, result.newKeyVersion),
              minimumReadableKeyVersion: Math.max(
                item.minimumReadableKeyVersion ?? 1,
                result.newKeyVersion,
              ),
              needsRotation: false,
            }
          : item
      )));
      // "Resetting encryption…" until the own grant for the new epoch is fetched and unwrapped.
      conversationsRef.current = conversationsRef.current.map((item) =>
        item.id === conversationId
          ? { ...item, keyVersion: result.newKeyVersion, minimumReadableKeyVersion: result.newKeyVersion }
          : item,
      );
      await finishResetUnlock(conversationId);
    } catch (err: any) {
      setResetPhase('confirm');
      if (err instanceof SettingsGateCancelled) return;
      setResetError(
        grantErrorCode(err) === 'key_version_conflict'
          ? KEY_VERSION_CONFLICT_MESSAGE
          : err?.message || 'Could not reset this conversation. Please try again.',
      );
    }
  }, [applyReadableFloor, buildResetGrants, finishResetUnlock, isOwner, resetExpectedVersion, resetGate, resetPhase, setMembers, studyId]);

  const dequeueApproval = useCallback(() => {
    setApprovalQueue((prev) => prev.slice(1));
    setApprovalError(null);
  }, []);

  const handleApprove = useCallback(async () => {
    const request = approvalQueue[0];
    if (!request || approvalBusy) return;
    const conversation = conversationsRef.current.find((c) => c.id === request.conversationId);
    const versions = ckCacheRef.current.get(request.conversationId);
    if (!conversation || !versions) return;
    setApprovalBusy(true);
    setApprovalError(null);
    try {
      const floor = conversation.minimumReadableKeyVersion ?? 1;
      const listed = (membersRef.current[request.conversationId] ?? []).find((m) => m.uid === request.uid);
      // Wrap for exactly the public key whose fingerprint the dialog showed.
      const grantee: MemberKeyInfo = {
        uid: request.uid,
        role: listed?.role ?? 'researcher',
        displayName: listed?.displayName ?? null,
        publicKey: request.publicKey,
        keyVersions: listed?.publicKey === request.publicKey ? listed.keyVersions : [],
        keyHistory: listed?.keyHistory ?? [],
        keyChangePending: true,
      };
      const held = Array.from(versions.keys());
      const grants = await buildGrantsForMember(
        versions,
        grantee,
        request.conversationId,
        versionsToGrant(held, grantee, floor),
      );
      await uploadGrants(studyId, request.conversationId, grants);
      dequeueApproval();
      try {
        setMembers(request.conversationId, (await getConversationKeys(studyId, request.conversationId)).members);
      } catch {
        /* the listing refreshes on the next event */
      }
    } catch (err: any) {
      if (grantErrorCode(err) === 'grant_stale') {
        // The member's key changed again: refetch and, if still pending, reopen with the new fingerprint.
        try {
          const keys = await getConversationKeys(studyId, request.conversationId);
          setMembers(request.conversationId, keys.members);
          const member = keys.members.find((m) => m.uid === request.uid);
          setApprovalQueue((prev) => prev.slice(1));
          if (member?.keyChangePending && member.publicKey && member.publicKey !== request.publicKey) {
            const shown = shownApprovalsRef.current.get(request.conversationId) ?? new Set<string>();
            shown.add(`${member.uid}:${member.publicKey}`);
            shownApprovalsRef.current.set(request.conversationId, shown);
            const fingerprint = formatFingerprint(await keyFingerprint(member.publicKey));
            setApprovalQueue((prev) => [
              {
                conversationId: request.conversationId,
                uid: member.uid,
                publicKey: member.publicKey as string,
                displayName: member.displayName || member.uid,
                fingerprint,
              },
              ...prev,
            ]);
          }
          setApprovalError(null);
        } catch (refetchError: any) {
          setApprovalError(refetchError?.message || 'Could not refresh the member list.');
        }
      } else {
        setApprovalError(err?.message || 'Could not approve the key change. Please try again.');
      }
    } finally {
      setApprovalBusy(false);
    }
  }, [approvalQueue, approvalBusy, studyId, dequeueApproval, setMembers]);

  const handleNotNow = useCallback(() => {
    const request = approvalQueue[0];
    if (!request) return;
    const suppressed = suppressedApprovalsRef.current.get(request.conversationId) ?? new Set<string>();
    suppressed.add(`${request.uid}:${request.publicKey}`);
    suppressedApprovalsRef.current.set(request.conversationId, suppressed);
    dequeueApproval();
  }, [approvalQueue, dequeueApproval]);

  const handleUnlocked = useCallback((keys: UserKeys) => {
    setMyKeys(keys);
    setReplacedNotice(null);
    setSessionEpoch((n) => n + 1);
    setGate('ready');
  }, []);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  let content: React.ReactNode;

  if (gate === 'setup') {
    content = (
      <KeySetupDialog
        open
        uid={uid}
        email={email}
        onComplete={handleUnlocked}
        onIdentityExists={() => setGateAttempt((attempt) => attempt + 1)}
        onUnsupported={() => {
          setGateError(PASSKEY_UNSUPPORTED_MESSAGE);
          setGate('error');
        }}
      />
    );
  } else if (gate === 'locked' && myKeys) {
    content = <UnlockGate uid={uid} email={email} keys={myKeys} notice={replacedNotice} onUnlocked={handleUnlocked} />;
  } else if (gate === 'error') {
    content = (
      <Alert
        severity="error"
        sx={{ maxWidth: 480, mx: 'auto', my: 6 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setGateError(null);
              setGateAttempt((attempt) => attempt + 1);
            }}
          >
            Retry
          </Button>
        }
      >
        {gateError || 'Could not open chat.'}
      </Alert>
    );
  } else if (gate !== 'ready') {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} aria-label="Opening chat" />
      </Box>
    );
  } else {
    const participantUid = selectedConversation?.participantId ?? '';
    const participantMember = selectedMembers?.find((m) => m.role === 'participant');
    const participantOutdated = participantMember != null && participantMember.publicKey === null;

    const selectedKeyState: ConvKeyState = (selectedId && keyStates[selectedId]) || { status: 'loading' };
    const selectedThread = (selectedId && threads[selectedId]) || undefined;

    const summaries: ChatConversationSummary[] = conversations.map((conversation) => {
      const members = membersInfo[conversation.id];
      const outdated = members?.some((m) => m.role === 'participant' && m.publicKey === null) ?? false;
      const thread = threads[conversation.id];
      const lastLoaded = thread ? [...thread].reverse().find((m) => !m.system) : undefined;
      let preview: string;
      if (outdated) preview = PARTICIPANT_APP_OUTDATED_MESSAGE;
      else if (lastLoaded && !lastLoaded.undecryptable) preview = lastLoaded.text;
      else if (conversation.lastMessageAt)
        preview = `Last message ${new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }).format(new Date(conversation.lastMessageAt))}`;
      else preview = 'No messages yet.';
      return {
        id: conversation.id,
        title: participantLabel(conversation.participantId),
        preview,
        unread: unreadIds.has(conversation.id),
        chatDisabled: outdated,
      };
    });

    // "Seen" caption: the participant's read marker has passed my LAST message.
    const participantLastReadAt = participantUid
      ? selectedConversation?.readState?.[participantUid]
      : undefined;
    const lastOwnMessage = selectedThread
      ? [...selectedThread].reverse().find((m) => m.senderId === uid && !m.failed && !m.system)
      : undefined;
    const seenMessageId =
      lastOwnMessage && parseTime(participantLastReadAt) >= parseTime(lastOwnMessage.createdAt)
        ? lastOwnMessage.id
        : null;

    const selfPending = selectedKeyState.status === 'self-pending';
    const threadMessages: ChatThreadMessage[] = selfPending
      ? []
      : (selectedThread ?? []).map((message) => ({
          id: message.id,
          sender: message.senderId === uid ? 'researcher' : message.senderRole,
          text: message.text,
          createdAt: message.createdAt,
          status: message.id === seenMessageId ? 'read' : message.senderId === uid ? 'sent' : undefined,
          undecryptable: message.undecryptable,
          failed: message.failed,
          system: message.system,
        }));

    const openResetFromSheet = () => {
      setInfoOpen(false);
      setResetError(null);
      setResetPhase('confirm');
      setResetExpectedVersion(selectedConversation?.keyVersion ?? null);
      setResetOpen(true);
    };

    let notice: ChatThreadNotice | null = null;
    if (selfPending) {
      notice = {
        severity: 'warning',
        title: SELF_PENDING_BANNER,
        body: isOwner ? <Typography variant="body2">{OWNER_SELF_PENDING_LINE}</Typography> : undefined,
      };
    } else if (selectedKeyState.status === 'pending') {
      notice = null; // plain "Access pending" text where the messages go
    } else if (selectedKeyState.status === 'error') {
      notice = { severity: 'error', title: selectedKeyState.error || 'Could not load the conversation keys.' };
    } else if (participantOutdated) {
      notice = { severity: 'warning', title: PARTICIPANT_APP_OUTDATED_MESSAGE };
    }

    const composerDisabled =
      !selectedId || selectedKeyState.status !== 'ready' || participantOutdated;
    const threadLoading =
      selectedKeyState.status === 'loading' || (selectedKeyState.status === 'ready' && !selectedThread);


    const currentApproval = approvalQueue[0];

    content = (
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderTop: '3px solid',
          borderTopColor: 'primary.main',
          borderRadius: 3,
          overflow: 'hidden',
          height: 640,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <StudySectionIcon><ChatBubbleOutline /></StudySectionIcon>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Participant Chat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Secure messaging with study participants
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<Settings size={16} aria-hidden />}
            onClick={() => setSettingsOpen(true)}
            disabled={idleLocked || !myKeys}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Encryption settings
          </Button>
        </Box>

        {listError && (
          <Alert severity="error" sx={{ m: 1.5 }}>
            {listError}
          </Alert>
        )}

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {idleLocked ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }} role="status">
              <Typography variant="body2" color="text.secondary">
                {IDLE_LOCKED_MESSAGE}
              </Typography>
            </Box>
          ) : loadingList && conversations.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={28} aria-label="Loading conversations" />
            </Box>
          ) : (
            <>
              <ConversationList conversations={summaries} activeId={selectedId} onSelect={setSelectedId} />
              {selectedConversation ? (
                <ChatThread
                  title={participantLabel(selectedConversation.participantId)}
                  messages={threadMessages}
                  seenMessageId={seenMessageId}
                  loading={!selfPending && threadLoading}
                  notice={notice}
                  emptyText={selectedKeyState.status === 'pending' ? ACCESS_PENDING_MESSAGE : undefined}
                  headerAction={
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Button
                        size="small"
                        startIcon={<ShieldCheck size={16} aria-hidden />}
                        onClick={() => setInfoOpen(true)}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Encryption
                      </Button>
                    </Box>
                  }
                  composerDisabled={composerDisabled}
                  hasMore={(selectedId && hasMore[selectedId]) || false}
                  loadingOlder={loadingOlder}
                  onLoadOlder={handleLoadOlder}
                  onSend={handleSend}
                />
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {conversations.length === 0
                      ? 'No participants are enrolled in this study yet.'
                      : 'Select a conversation.'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        <EncryptionResetDialog
          open={resetOpen && Boolean(selectedConversation)}
          participantId={selectedConversation?.participantId ?? ''}
          phase={resetPhase}
          error={resetError}
          onClose={() => {
            if (resetPhase === 'resetting' && !resetError) return;
            setResetOpen(false);
            setResetExpectedVersion(null);
            setResetError(null);
            setResetPhase('confirm');
          }}
          onConfirm={handleEncryptionReset}
          signInStep={resetGate.signInStep}
          onRetry={handleResetRetry}
        />
        <KeyChangeApprovalDialog
          open={currentApproval != null && !idleLocked}
          memberName={currentApproval?.displayName ?? ''}
          fingerprint={currentApproval?.fingerprint ?? ''}
          busy={approvalBusy}
          error={approvalError}
          onApprove={handleApprove}
          onNotNow={handleNotNow}
        />
        <EncryptionInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} onResetChat={isOwner ? openResetFromSheet : undefined} />
        {myKeys && (
          <ChatKeySettings
            open={settingsOpen && !idleLocked}
            uid={uid}
            email={email}
            keys={myKeys}
            onClose={() => setSettingsOpen(false)}
            onKeysChange={setMyKeys}
          />
        )}
      </Paper>
    );
  }

  return (
    <Box className="ph-no-capture" data-testid="chat-tab-root">
      {content}
    </Box>
  );
}
